import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { sessionService } from "redux-react-session";
import { Box } from "@mui/material";

import { useSession } from "../../hooks/useSession";
import {
  authOptionsFooterDivider,
  callAPIInterface,
  getCurrentTimeInSecond,
  handleAfterLoginProcess,
  handleContactSupport,
  mfaSetupOptions,
  setUpdatedSessionData,
} from "../constants";
import { showToast } from "../../redux/actions";
import {
  authAppCodeSubtitle,
  contactSupport,
  loadingMsg,
  mfaResendCodeMsg,
  needAnyHelp,
  otpNotReceived,
  otpWillExpireMsg,
  resend,
  twoFactorVerificationHeader,
  twoFactorVerificationMessage,
  twoStepVerification,
  useAnotherMethod,
  verify,
} from "../messages";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import Text from "../text/Text";
import Label from "../label/Label";
import { OTPInput } from "../otpInput/OTPInput";
import { CustomLink } from "../link/Link";
import Button from "../button/Button";
import CommonAuthHeader from "../options/CommonAuthHeader";
import CommonHeader from "../CommonHeader";
import SelectAuthenticationMethodDrawer from "./SelectAuthenticationMethodDrawer";

const TwoFactorAuthentication = ({ isFromOptions = false }) => {
  const { session, error } = useSession();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [otp, setOtp] = useState("");
  const [isDisabled, setIsDisabled] = useState(true);
  const [timerCount, setTimerCount] = useState();
  const [expiresAt, setExpiresAt] = useState();
  const [isExpiredOTP, setIsExpiredOTP] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginMfaAuthDetails, setLoginMfaAuthDetails] = useState([]);
  const [mfaOptions, setMfaOptions] = useState([]);
  const [selectedMfaMethod, setSelectedMfaMethod] = useState(null);
  const [selectAuthMethodModal, setSelectAuthMethodModal] = useState(false);

  const isFromSetupPage = location?.state?.from === "/two-factor-setup";
  const isFromLoginPage = location?.state?.from === "/login";
  const isFromVerifyEmailPage = location?.state?.isFromVerifyEmailPage;

  useEffect(() => {
    if (!(isFromLoginPage || isFromSetupPage)) {
      navigate(-1);
    }
  }, []);

  const setTimerStates = (timerExpireAt) => {
    const currentSeconds = getCurrentTimeInSecond(timerExpireAt);
    setTimerCount(currentSeconds);
    currentSeconds === 0 && setIsExpiredOTP(true);
  };

  useEffect(() => {
    if (session) {
      if (session.mfa_configurations) {
        const configuration = session.mfa_configurations;
        const preferredMFAMethod = getPreferredMfaMethod(configuration);

        setSelectedMfaMethod(preferredMFAMethod);
        setLoginMfaAuthDetails(configuration);

        const expiresAt = session.otp_expires_at;
        if (preferredMFAMethod?.mfa_type === "sms" && expiresAt) {
          setExpiresAt(expiresAt);
          setTimerStates(expiresAt);
        }
      } else {
        navigate(-1);
      }
    }

    if (error) {
      navigate(-1);
    }
  }, [session, error]);

  useEffect(() => {
    if (expiresAt && timerCount > 0) {
      const timer = setInterval(() => {
        setTimerStates(expiresAt);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [expiresAt, timerCount]);

  const getPreferredMfaMethod = (mfaList = []) => {
    if (isFromSetupPage) {
      setMfaOptions(mfaSetupOptions);
      return mfaList[0];
    }

    const enabled = mfaList.filter(
      (item) =>
        (item.mfa_type === "sms" && item.is_mfa_phone_number_verified) ||
        (item.mfa_type === "google_authenticator" &&
          item.is_google_auth_enabled)
    );

    setMfaOptions(enabled);

    if (enabled.length === 0) return null;

    const preferred = enabled.find((item) => item.is_preferred_mfa);
    return preferred || enabled[0];
  };

  const handleOtpChange = (newOtp) => {
    setOtp(newOtp);
    setIsDisabled(newOtp.length < 6);
  };

  const handleSubmitVerifyOTP = async (mfaMethod) => {
    try {
      setIsSubmitting(true);
      const OTPData = {
        session: session.session,
        otp,
      };

      let mfaAPIEndpoint = "";
      if (mfaMethod.mfa_type === "sms") {
        mfaAPIEndpoint = isFromSetupPage
          ? "/login/mfa/phone-number/verify"
          : "/login/mfa/phone-number/verify-otp";
      } else if (mfaMethod.mfa_type === "google_authenticator") {
        mfaAPIEndpoint = isFromSetupPage
          ? "/login/mfa/google-auth/verify"
          : "/login/mfa/google-auth/verify-otp";
      }

      const response = await callAPIInterface("POST", mfaAPIEndpoint, OTPData);

      await setUpdatedSessionData(response);
      const sessionState = response?.session_state;

      if (sessionState === "trusted_device_in_progress") {
        navigate("/unrecognized-device");
        return;
      }

      if (isFromVerifyEmailPage) {
        await sessionService.deleteUser();
      } else {
        await handleAfterLoginProcess(navigate);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onChangeMethod = async (mfaMethod) => {
    setSelectAuthMethodModal(false);
    setOtp("");

    if (isFromSetupPage) {
      navigate("/two-factor-setup", {
        state: {
          from: "/two-factor",
          mfa_type: mfaMethod.mfa_type,
        },
      });
      return;
    }

    if (!expiresAt && mfaMethod.mfa_type === "sms") {
      setIsSubmitting(true);
      await handleResendCode();
      setIsSubmitting(false);
    }

    setSelectedMfaMethod(mfaMethod);
  };

  const handleResendCode = async () => {
    try {
      const mfaAPIEndpoint = isFromSetupPage
        ? "/login/mfa/phone-number/resend-verification-code"
        : "/login/mfa/phone-number/resend-otp";

      const response = await callAPIInterface("POST", mfaAPIEndpoint, {
        session: session.session,
      });

      if (response) {
        sessionService.saveSession({
          ...session,
          otp_expires_at: response.otp_expires_at,
        });
        setExpiresAt(response.otp_expires_at);
        setTimerCount(getCurrentTimeInSecond(response?.otp_expires_at));
        dispatch(
          showToast({
            isToastOpen: true,
            toastMessage: mfaResendCodeMsg(!isFromSetupPage),
            toastVariant: "success",
          })
        );

        setIsExpiredOTP(false);
        setOtp("");
        setIsDisabled(true);
      }
    } catch (error) {}
  };

  const smsConfigDetails = useMemo(() => {
    return (
      loginMfaAuthDetails &&
      loginMfaAuthDetails.filter((item) => item.mfa_type === "sms")?.[0]
    );
  }, [loginMfaAuthDetails]);

  const showAnotherMethod =
    ((isFromSetupPage &&
      selectedMfaMethod?.mfa_type === "google_authenticator") ||
      isFromLoginPage) &&
    mfaOptions?.length > 1;

  const mfaMethodContent = {
    sms: {
      title: twoFactorVerificationHeader,
      subheader: (
        <>
          {twoFactorVerificationMessage[0]}{" "}
          <Text component="span" size={14} className="default-text" font="bold">
            {smsConfigDetails?.mfa_phone_number?.substr(
              smsConfigDetails?.mfa_phone_number.length - 4
            )}
          </Text>{" "}
          {twoFactorVerificationMessage[1]}{" "}
          <Text component="span" size={14} className="default-text" font="bold">
            {session?.email}
          </Text>
          .
        </>
      ),
    },
    google_authenticator: {
      title: twoStepVerification,
      subheader: authAppCodeSubtitle,
    },
  };

  return (
    <>
      <BackdropLoader
        open={isSubmitting}
        alt={loadingMsg}
        text={loadingMsg}
        customClass="loading-in-auth"
      />
      <CommonHeader icon="backArrow" />
      <CommonAuthHeader showSpeedLogo={false} sx={{ height: "100%" }}>
        <Box className="two-step-auth-wrapper-content">
          <Box className="auth-wrapper-body">
            <Box>
              <Text className="default-text" size={22} font="bold">
                {mfaMethodContent[selectedMfaMethod?.mfa_type]?.title}
              </Text>
              <Text className="grey-text" size={14}>
                {mfaMethodContent[selectedMfaMethod?.mfa_type]?.subheader}
              </Text>
              <Label className="mt-24">Code</Label>
              <OTPInput value={otp} onChange={handleOtpChange} numInputs={6} />
              {selectedMfaMethod?.mfa_type === "sms" && timerCount > 0 && (
                <Text
                  sx={{ color: "#667085", mt: "58px" }}
                  size={14}
                  align="center"
                >
                  {otpWillExpireMsg}{" "}
                  <Text sx={{ color: "#0A0A0A" }} component="span" font="bold">
                    {`${Math.floor(timerCount / 60)}`.padStart(2, 0)}:
                    {`${timerCount % 60}`.padStart(2, 0)}
                  </Text>
                </Text>
              )}
              {selectedMfaMethod?.mfa_type === "sms" && isExpiredOTP && (
                <Text
                  className="default-text"
                  sx={{ mt: "58px" }}
                  size={14}
                  align="center"
                >
                  {otpNotReceived}{" "}
                  <CustomLink bold onClick={handleResendCode} size="small">
                    {resend}
                  </CustomLink>
                </Text>
              )}
              {showAnotherMethod && (
                <Box textAlign="center" marginTop={3}>
                  <Text
                    variant="subtitle1"
                    font="bold"
                    size={16}
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectAuthMethodModal(true);
                    }}
                    className="blue-text mt-8"
                  >
                    {useAnotherMethod}
                  </Text>
                </Box>
              )}
            </Box>
            <Box textAlign="center" marginTop="20px">
              <Text
                component="span"
                size={14}
                className="default-text"
                font="regular"
              >
                {needAnyHelp}
              </Text>
              <CustomLink
                sx={{
                  fontWeight: 600,
                }}
                onClick={handleContactSupport}
                color="primary"
              >
                {contactSupport}
              </CustomLink>
            </Box>
          </Box>
          {isFromOptions && authOptionsFooterDivider}
          <Box className={!isFromOptions ? "footer" : ""} left={0}>
            <Button
              fullWidth
              sx={!isFromOptions && { m: "14px 20px" }}
              variant="contained"
              label={verify}
              onClick={() => {
                handleSubmitVerifyOTP(selectedMfaMethod);
              }}
              disabled={isDisabled}
              {...(!isFromOptions && { textClass: "font-14" })}
            />
          </Box>
        </Box>

        <SelectAuthenticationMethodDrawer
          openModal={selectAuthMethodModal}
          setOpenModal={setSelectAuthMethodModal}
          mfaConfigurations={mfaOptions.filter(
            (mfa) => mfa.mfa_type !== selectedMfaMethod?.mfa_type
          )}
          isFromBrowser={isFromOptions}
          onChangeMethod={onChangeMethod}
        />
      </CommonAuthHeader>
    </>
  );
};

export default TwoFactorAuthentication;
